// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MyCustomQuotesRelatedLine from "@bg/App/Components/MyCustomQuotes/CustomQuoteRelatedQuoteLine";
import LineNotes from "@bg/App/Components/MyCustomQuotes/LineNotes";
import QuoteLineImageCarousel from "@bg/App/Components/RfqQuoteDetails/QuoteLineImageCarousel";
import deleteLine from "@bg/App/Handlers/CustomQuote/DeleteCustomQuoteLine";
import setSelectedLines from "@bg/App/Handlers/CustomQuote/SetSelectedLines";
import updateLineNotes from "@bg/App/Handlers/CustomQuote/UpdateLineNotes";
import updateLinePrice from "@bg/App/Handlers/CustomQuote/UpdateLinePrice";
import updateLineQty from "@bg/App/Handlers/CustomQuote/UpdateLineQty";
import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import ProductAddToCartButton from "@bg/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import {
    MyListsDetailsProductListLineStyles,
    productListLineStyles,
} from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: CustomQuoteModel;
    quoteLine: GroupLineModel;
    groupName: string;
    addRelatedProduct: (lineId: GroupLineModel, group: any) => void;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const productInfo: ProductInfo = state.myCustomQuoteDetails.productsInfo[ownProps.quoteLine.lineId];
    return {
        settingsCollection: getSettingsCollection(state),
        productInfo,
        realTimeInventoryFailed: state.myCustomQuotes.realTimeInventoryFailed,
        realTimePricingFailed: state.myCustomQuotes.realTimePricingFailed,
        selectedLineIds: state.myCustomQuoteDetails.selectedLineIds,
        sortBy: state.myCustomQuoteDetails.sortBy,
    };
};

const mapDispatchToProps = {
    setSelectedLines,
    updateLineNotes,
    updateLineQty,
    deleteLine,
    updateLinePrice,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyCustomQuoteDetailsLineStyles extends MyListsDetailsProductListLineStyles {
    relatedItemsWrapper?: InjectableCss;
    toggleLink?: LinkPresentationProps;
    addRelatedProductLink?: LinkPresentationProps;
    calculateMargin?: LinkPresentationProps;
    marginWrapper?: InjectableCss;
    quotePriceInput?: InjectableCss;
    mobileHiddenActions?: InjectableCss;
    marginAndMsgWrapper?: InjectableCss;
    errorMsg?: TypographyPresentationProps;
}

export const styles: MyCustomQuoteDetailsLineStyles = {
    ...productListLineStyles,
    lineInnerContainer: {
        css: css`
            ${productListLineStyles.lineInnerContainer?.css}
            &&&&.related-item {
                & > div:first-child {
                    max-width: 600px;
                }
            }
            &&& .hide-on-small {
                @media (max-width: 481px) {
                    display: none;
                }
            }
        `,
    },
    leftSection: {
        css: css`
            ${productListLineStyles?.leftSection?.css}
            max-width: 680px;
        `,
    },
    buttonsGridItem: {
        css: css`
            ${productListLineStyles.buttonsGridItem?.css}
            &.flex-grow {
                justify-content: flex-end;
            }

            @media (max-width: 481px) {
                &&& .default,
                &&& .loading {
                    width: 200px;
                }
            }
        `,
    },
    uomAndQuantityInnerContainer: {
        css: css`
            ${productListLineStyles.uomAndQuantityInnerContainer?.css}
            display: flex;
            align-items: center;
            @media (max-width: 481px) {
                order: 4;
            }
        `,
    },
    addRelatedProductLink: linkIconStyleGenerator({
        icon: "Search",
        textSize: `${fontConfig.fontSize.xSmall}px`,
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    calculateMargin: linkIconStyleGenerator({
        icon: "Calculator",
        textSize: `${fontConfig.fontSize.xSmall}px`,
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    relatedItemsWrapper: {
        css: css`
            &&&.last-item .related-item:last-child {
                border-bottom: 3px solid ${getColor("common.ui3")};
            }
        `,
    },
    priceGridItem: {
        css: css`
            ${productListLineStyles.priceGridItem?.css}
            flex-direction: column;
            align-items: flex-start;
            section {
                align-items: flex-start;
                & > div {
                    text-align: left;
                }
            }
            legend.custom-quote {
                display: block !important;
                font-size: ${fontConfig.fontSize.legend}px;
                line-height: ${fontConfig.lineHeight.legend};
                margin-bottom: 3px;
            }
            legend:not(.custom-quote) {
                display: none !important;
            }
            @media (max-width: 481px) {
                order: 1;
            }
        `,
    },
    lineTotal: {
        css: css`
            ${productListLineStyles.lineTotal?.css}
            text-align: right;
            @media (max-width: 481px) {
                order: 3;
                width: 50%;
                min-width: 50%;
            }
        `,
    },
    notesGridItem: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: space-between;
            @media (max-width: 768px) {
                margin-top: 15px;
            }
            @media (max-width: 481px) {
                margin: 0;
                flex-direction: column;
                align-items: flex-start;
                && button {
                    margin-top: 20px;
                }
            }
        `,
    },
    toggleLink: linkIconStyleGenerator({
        icon: "Create",
        textSize: "13px",
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    marginAndMsgWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    marginWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            margin: 8px 0;
            legend {
                display: block !important;
                font-size: ${fontConfig.fontSize.legend}px;
                line-height: ${fontConfig.lineHeight.legend};
                margin-bottom: 3px;
            }
            @media (max-width: 481px) {
                margin: 0;
            }
        `,
    },
    errorMsg: {
        size: fontConfig.fontSize.xSmall,
        color: "danger",
        css: css`
            margin-left: 10px;
            display: block;
            margin-top: 10px;
            @media (max-width: 481px) {
                margin-left: 0;
            }
        `,
    },
    quotePriceInput: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            padding: 0 30px 0 20px;
            max-width: 130px;
            height: 36px;
            background: ${getColor("common.ui4")};
            border: 1px solid transparent;
            border-radius: 4px;
            &:focus-within {
                border-color: ${getColor("text.main")};
            }
            &&& input {
                min-height: 34px !important;
                height: 34px !important;
                padding: 0;
                border: 0 !important;
                &:focus {
                    padding: 0;
                }
            }
            label {
                position: absolute;
                &:first-child {
                    left: 7px;
                }
                &:last-child {
                    right: 10px;
                    font-size: ${fontConfig.fontSize.legend}px;
                }
            }
        `,
    },
    mobileHiddenActions: {
        css: css`
            display: none;
            @media (max-width: 481px) {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                justify-content: space-between;
                margin: 20px 0;
                order: 2;
                width: 100%;
                & button {
                    padding-top: 10px;
                    white-space: nowrap;
                }
            }
        `,
    },
};

const MyCustomQuotesQuoteLine: React.FC<Props> = ({
    quote,
    quoteLine,
    settingsCollection,
    groupName,
    selectedLineIds,
    productInfo,
    realTimeInventoryFailed,
    realTimePricingFailed,
    setSelectedLines,
    updateLineNotes,
    updateLineQty,
    deleteLine,
    addRelatedProduct,
    updateLinePrice,
    sortBy,
}) => {
    const dispatch = useAppDispatch();

    const product = quoteLine.productResult;
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [showLineNote, setShowLineNotes] = useState(!!quoteLine.notes);
    const [isLineSelected, setIsSelected] = useState(false);
    const [quotePrice, setQuotePrice] = useState(quoteLine.extendedPrice);
    const [calculateMarginModalIsOpen, setCalculateMarginModalIsOpen] = useState(false);
    const [marginPercent, setMarginPercent] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [lineImages, setLineImages] = useState<any>();

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setSelectedLines({ groupName, lineIds: [quoteLine.lineId] });
    };

    // JS calculates margin 2.55*100 = 254.99996
    // parse it as float with toFixed(2) to do the round
    useEffect(() => {
        if (!quoteLine) {
            return;
        }
        setMarginPercent(parseFloat((quoteLine.margin * 100).toFixed(2)).toString());
    }, [quoteLine]);

    useEffect(() => {
        if (quoteLine) {
            let images = [] as any;
            if (quoteLine.productResult.images) {
                images = quoteLine.productResult.images.map(i => ({
                    id: i.id,
                    imagePath: i.smallImagePath || i.mediumImagePath || i.largeImagePath,
                }));
            }
            setLineImages(images);
            setQuotePrice(quoteLine.extendedPrice);
        }
    }, [quoteLine]);

    useEffect(() => {
        return () => {
            dispatch({
                type: "Pages/MyCustomQuotes/ClearProductsQty",
            });
        };
    }, []);

    useEffect(() => {
        if (selectedLineIds && selectedLineIds?.hasOwnProperty(groupName)) {
            setIsSelected(selectedLineIds[groupName].indexOf(quoteLine.lineId) > -1);
        } else {
            setIsSelected(false);
        }
    }, [groupName, quoteLine.lineId, selectedLineIds]);

    const deleteQuotetLine = () => {
        setIsDeleting(true);

        deleteLine({
            customQuoteId: quote.id,
            lineId: quoteLine.lineId,
            onComplete: (result: any) => {
                if (result.successful) {
                    setSelectedLines({ groupName, lineIds: [quoteLine.lineId] });
                    setIsDeleting(false);
                }
            },
        });
    };

    const toggleLineNotes = () => {
        setShowLineNotes(!showLineNote);
    };

    const updateQuoteLineNotes = (notes: string) => {
        if (notes === quoteLine.notes) {
            return;
        }
        updateLineNotes({
            customQuoteId: quote.id,
            linesModel: [{ lineId: quoteLine.lineId, Note: notes }],
            updateNote: true,
            onComplete: (result: any) => {
                setShowLineNotes(!!notes);
            },
        });
    };

    const onQuotePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setQuotePrice("");
            return;
        }
        setQuotePrice(event.target.value);
    };

    const onQuotePriceBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseFloat(event.target.value);
        if (Number.isNaN(price)) {
            setQuotePrice(quoteLine.extendedPrice);
            return;
        }
        setQuotePrice(price.toString());
        if (quoteLine.extendedPrice && event.target.value === quoteLine.extendedPrice.toString()) {
            return;
        }
        updateLinePrice({
            customQuoteId: quote.id,
            updatePrice: true,
            linesModel: [{ lineId: quoteLine.lineId, price: Number(price.toFixed(2)) }],
            onSuccess: result => {
                let updatedLine = null;
                for (const group in result.quote.groupLinesModel) {
                    if (result.quote.groupLinesModel[group]) {
                        updatedLine = result.quote.groupLinesModel[group].find(
                            (line: any) => line.lineId === quoteLine.lineId,
                        );
                        if (updatedLine) {
                            break; // Exit the loop once we find the line
                        }
                    }
                }
                if (updatedLine) {
                    setQuotePrice(updatedLine.extendedPrice); // Update the quotePrice with the new value
                }
            },
        });
    };

    const handleMarginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^[0-9\b]+$/.test(event.target.value) && event.target.value !== "") {
            return;
        }
        setMarginPercent(event.target.value);
    };

    const handleMarginBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseInt(event.target.value, 10);
        if (Number.isNaN(price)) {
            setMarginPercent("0");
            return;
        }
        setMarginPercent(price.toString());
    };

    // JS calculates margin 2.55*100 = 254.99996
    // parse it as float with toFixed(2) to do the round
    const handleCloseCalculateMarginModal = () => {
        setMarginPercent(parseFloat((quoteLine.margin * 100).toFixed(2)).toString());
        setCalculateMarginModalIsOpen(false);
    };

    const applyMargin = () => {
        if (!productInfo) {
            return;
        }
        setCalculateMarginModalIsOpen(false);
        const margin = parseFloat(marginPercent) / 100;
        const price = quoteLine.extendedPrice ? Number(quoteLine.extendedPrice) : productInfo?.pricing?.unitNetPrice;
        updateLinePrice({
            customQuoteId: quote.id,
            updateMargin: true,
            margin,
            updatePrice: !quoteLine.extendedPrice || undefined,
            linesModel: [{ lineId: quoteLine.lineId, price }],
            onSuccess: result => {
                let updatedLine = null;
                for (const group in result.quote.groupLinesModel) {
                    if (result.quote.groupLinesModel[group]) {
                        updatedLine = result.quote.groupLinesModel[group].find(
                            (line: any) => line.lineId === quoteLine.lineId,
                        );
                        if (updatedLine) {
                            break; // Exit the loop once we find the line
                        }
                    }
                }
                if (updatedLine) {
                    setQuotePrice(updatedLine.extendedPrice); // Update the quotePrice with the new value
                }
            },
        });
    };

    const onQtyOrderedChanged = (qty: number) => {
        dispatch({
            type: "Pages/MyCustomQuotes/UpdateProductsQty",
            lineModel: {
                lineId: quoteLine.lineId,
                qty,
                originalQty: quoteLine.qty,
            },
        });
    };

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo: { ...productInfo, qtyOrdered: quoteLine.qty },
        onQtyOrderedChanged,
        onUnitOfMeasureChanged: unitOfMeasure => {},
    };

    // print line qr code
    const printItem = () => {
        printItems(`#qrCode_${quoteLine.lineId} canvas`, quoteLine.productResult, [productInfo!], true, true);
    };

    const lineLabel = showLineNote ? "Hide Notes" : quoteLine.notes.length ? "Show Notes" : "Line Notes";
    const qtyOutsideBreakPrice = useMemo(
        () => quote.isCameFromNormalQuote && quoteLine.breakPricesRfqModel && !quoteLine.customerPrice,
        [quote.isCameFromNormalQuote, quoteLine.breakPricesRfqModel, quoteLine.customerPrice],
    );

    if (!productInfo) {
        return null;
    }

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper
                {...styles.lineInnerContainer}
                id={`contentItem_${quoteLine.lineId}`}
                data-test-selector={`${product.id}`}
                className={`no-borders ${quoteLine.relatedItems?.length > 0 ? "has-related-products" : ""} ${
                    quoteLine.sortOrder === quote.groupLinesModel?.[groupName]?.length ? "last-line" : ""
                }`}
            >
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.imageGridItem}>
                        <StyledWrapper {...styles.dragGrid}>
                            <Checkbox
                                {...styles.selectedCheckbox}
                                checked={isLineSelected}
                                onChange={selectChangeHandler}
                                data-test-selector="selectItem"
                            />
                            {!sortBy && (
                                <StyledWrapper {...styles.dragIconWrapper}>
                                    <Icon {...styles.dragHandleIcon} className="drag-handle" />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>

                        {/* <ProductImage product={productContext} extendedStyles={styles.productImageStyles} /> */}
                        <QuoteLineImageCarousel
                            productId={quoteLine.productResult.id}
                            productImages={lineImages}
                            productUrl={quoteLine.productResult.canonicalUrl}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.descriptionGridItem}>
                        <ProductDescription
                            product={productContext}
                            lines={2}
                            lineHeight={22}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                        <StyledWrapper {...styles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={product.detail?.sku || product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        {!quoteLine.isSpecItem && (
                            <StyledWrapper {...styles.availabilityGridItem}>
                                {productSettings.showInventoryAvailability && (
                                    <ProductContextAvailability
                                        extendedStyles={styles.productAvailabilityStyles as any}
                                    />
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper
                            css={css`
                                margin-top: 15px;
                                @media (max-width: 481px) {
                                    margin: 0;
                                }
                            `}
                        >
                            <Link
                                onClick={() => addRelatedProduct(quoteLine, groupName)}
                                {...styles.addRelatedProductLink}
                            >
                                {translate("Add Related Product")}
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.rightSectionTop}>
                        <StyledWrapper {...styles.priceAndAvailabilityInnerContainer}>
                            <StyledWrapper {...styles.priceGridItem}>
                                <legend className="custom-quote">{translate("My Price")}</legend>
                                {!quoteLine.breakPricesRfqModel &&
                                !quoteLine.customerPrice &&
                                productInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() !== "true" ? (
                                    <ProductPrice
                                        product={productContext}
                                        showSavings={false}
                                        extendedStyles={styles.productPriceStyles as any}
                                    />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        weight={fontConfig.weight.bold}
                                        css={css`
                                            width: 100%;
                                            text-align: left;
                                            margin-bottom: 0;
                                            @media (max-width: 1024px) {
                                                text-align: left;
                                            }
                                            legend {
                                                font-size: ${fontConfig.fontSize.legend}px;
                                                font-weight: ${fontConfig.weight.bold};
                                                text-transform: uppercase;
                                            }
                                        `}
                                    >
                                        {quoteLine.customerPrice &&
                                        (quoteLine.breakPricesRfqModel || quoteLine.isSpecItem) ? (
                                            <LocalizedCurrency amount={quoteLine.customerPrice} />
                                        ) : (
                                            "N/A"
                                        )}
                                    </Typography>
                                )}
                            </StyledWrapper>
                            <Hidden {...styles.mobileHiddenActions}>
                                <StyledWrapper {...styles.marginWrapper}>
                                    <legend>{translate("Quote Price")}</legend>
                                    <StyledWrapper {...styles.quotePriceInput}>
                                        <label>{translate("$")}</label>
                                        <TextField
                                            type="text"
                                            value={quotePrice}
                                            onChange={onQuotePriceChange}
                                            onBlur={onQuotePriceBlur}
                                            maxLength={10}
                                        />
                                        <label>/{product.unitOfMeasures?.[0].unitOfMeasureDisplay}</label>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <Link {...styles.calculateMargin} onClick={() => setCalculateMarginModalIsOpen(true)}>
                                    {translate("Calculate Margin")}
                                </Link>
                            </Hidden>
                            <StyledWrapper {...styles.uomAndQuantityInnerContainer}>
                                {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                    <StyledWrapper {...styles.uomGridItem}>
                                        <ProductUnitOfMeasureSelect
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.quantityGridItem}>
                                    <ProductQuantityOrdered
                                        {...({ changeOnBlur: true } as any)}
                                        extendedStyles={styles.productQuantityOrderedStyles as any}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.lineTotal}>
                                <Typography variant="legend">{translate("Total")}</Typography>
                                <Typography {...styles.priceTotal}>
                                    <LocalizedCurrency amount={quoteLine.totalQuotePrice} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.buttonsGridItem}
                            className={quote.isCameFromNormalQuote ? "flex-grow" : ""}
                        >
                            {!quote.isCameFromNormalQuote && (
                                <ProductAddToCartButton
                                    notes={quoteLine.notes}
                                    iconOnly={false}
                                    sizeVariant="small"
                                    extendedStyles={styles.addToCartButton}
                                    data-test-selector="addToCart"
                                />
                            )}
                            <Button
                                {...styles.deleteLink}
                                onClick={() => setDeleteLineModalIsOpen(true)}
                                data-test-selector="deleteListItem"
                            >
                                <ButtonIcon src="Delete" size={18} color="secondary" />
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {productInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() === "true" && (
                        <StyledWrapper
                            css={css`
                                @media (max-width: 481px) {
                                    margin-top: 8px;
                                }
                            `}
                        >
                            <Typography
                                color="danger"
                                size={fontConfig.fontSize.xSmall}
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                            >
                                {siteMessage("Custom_Price_FailedToLoad")}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.marginAndMsgWrapper}>
                        <StyledWrapper {...styles.marginWrapper} className="hide-on-small">
                            <legend>{translate("Quote Price")}</legend>
                            <StyledWrapper {...styles.quotePriceInput}>
                                <label>{translate("$")}</label>
                                <TextField
                                    type="text"
                                    value={quotePrice}
                                    onChange={onQuotePriceChange}
                                    onBlur={onQuotePriceBlur}
                                    maxLength={10}
                                />
                                <label>/{product.unitOfMeasures?.[0].unitOfMeasureDisplay}</label>
                            </StyledWrapper>
                        </StyledWrapper>
                        {qtyOutsideBreakPrice && (
                            <Typography {...styles.errorMsg}>
                                {siteMessage("Custom_CustomQuote_SpecificRangeProduct")}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.notesGridItem}>
                        <Link
                            {...styles.calculateMargin}
                            className="hide-on-small"
                            onClick={() => setCalculateMarginModalIsOpen(true)}
                        >
                            {translate("Calculate Margin")}
                        </Link>
                        <Link {...styles.printQrCode} onClick={printItem}>
                            {translate("Print QR Code")}
                        </Link>
                        <Link
                            {...styles.toggleLink}
                            onClick={toggleLineNotes}
                            data-test-selector="customQuoteLine_notesShowHideButton"
                        >
                            {translate(lineLabel)}
                        </Link>
                        <LineNotes
                            showNotes={showLineNote}
                            notes={quoteLine.notes}
                            onNotesChange={updateQuoteLineNotes}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: none;
                    `}
                    className="canvas-wrapper"
                    id={`qrCode_${quoteLine.lineId}`}
                >
                    <QRCode
                        value={`${window.location.origin}${product.canonicalUrl}`}
                        logoWidth={250}
                        logoHeight={250}
                    />
                </StyledWrapper>
            </StyledWrapper>
            {quoteLine.relatedItems && quoteLine.relatedItems.length > 0 && (
                <StyledWrapper
                    {...styles.relatedItemsWrapper}
                    className={quoteLine.sortOrder === quote.groupLinesModel?.[groupName]?.length ? "last-item" : ""}
                >
                    {quoteLine.relatedItems.map(related => (
                        <MyCustomQuotesRelatedLine
                            key={`relatedQuoteLine_${related.lineId}`}
                            quote={quote}
                            quoteLine={related}
                            groupName={groupName}
                            parentQuoteLineId={quoteLine.lineId}
                        />
                    ))}
                </StyledWrapper>
            )}
            <Modal
                size={400}
                isOpen={calculateMarginModalIsOpen}
                handleClose={handleCloseCalculateMarginModal}
                headline={translate("Add Price Margin")}
            >
                <TextField
                    type="text"
                    label={translate("Margin %")}
                    value={marginPercent}
                    onChange={handleMarginChange}
                    onBlur={handleMarginBlur}
                />

                <StyledWrapper className="modal-actions">
                    <Button onClick={applyMargin}>{translate("Apply")}</Button>
                    <Button variant="secondary" onClick={handleCloseCalculateMarginModal}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteQuotetLine}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyCustomQuotesQuoteLine);
